import { _isNil, _notNil } from '@/littledash';
import type { FC } from 'react';
import type {
  AuditLogEntitySummary as Summary,
  AuditLogEntityType,
} from '@/components/Studies/AuditLog/AuditLog.model';
import { AuditLogEntityDisplay, AuditLogEntityPropRow } from './AuditLogEntityDisplayComponents';

/**
 * Copy shown when the study-scoped entity lookup missed. ANIMAL rows point at
 * the animal itself; the four child types (SAMPLE / DOSAGE / OBSERVATION /
 * MEASUREMENT) hard-delete-and-recreate when the animal moves study, so their
 * "missing" state means the animal was moved and the child rows were dropped.
 */
const notInStudyMessage = (entityType: AuditLogEntityType | null | undefined): string => {
  switch (entityType) {
    case 'ANIMAL':
      return 'This animal is no longer in this study — showing historical audit data only.';
    case 'SAMPLE':
    case 'DOSAGE':
    case 'OBSERVATION':
    case 'MEASUREMENT':
      return 'The associated animal is no longer in this study — showing historical audit data only.';
    default:
      return 'This item is no longer in this study — showing historical audit data only.';
  }
};

interface AuditLogEntitySummaryProps {
  summary: Summary | undefined;
}

export const AuditLogEntitySummary: FC<AuditLogEntitySummaryProps> = ({ summary }) => {
  if (_isNil(summary)) {
    return null;
  }

  const { entity_type: entityType, entity, deleted_at: deletedAt } = summary;
  // Backend returns entity_in_study=false when the study-scoped lookup missed —
  // hard-deleted, moved to another study, or never in this study. Reason is
  // intentionally not disclosed by the API to avoid leaking cross-study info.
  const notInStudy = summary.entity_in_study === false;

  const hasContent = _notNil(entity) || _notNil(deletedAt) || notInStudy;
  if (!hasContent) {
    return null;
  }

  const hasResolvedDetails = _notNil(entityType) && _notNil(entity);

  return (
    <div data-test-component="AuditLogEntitySummary" data-testid="audit-log-entity-summary">
      {notInStudy ? (
        <p className="f6 dark-gray mb2" data-testid="audit-log-entity-summary__not-in-study">
          {notInStudyMessage(entityType)}
        </p>
      ) : hasResolvedDetails ? (
        <p className="f6 dark-gray mb2">Details reflect the item's current state.</p>
      ) : null}
      {hasResolvedDetails || _notNil(deletedAt) ? (
        <div className="ui-card pa3 bg-washed-blue">
          {hasResolvedDetails ? <AuditLogEntityDisplay summary={summary} /> : null}
          {_notNil(deletedAt) ? (
            <AuditLogEntityPropRow label="Deleted" value={deletedAt} type="datetime" valueClassName="red" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
